import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Link as JoyLink,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from '@mui/joy';
import { MdOutlineHistory } from 'react-icons/md';
import type { Project } from '../../types/project';
import { workspaceLifecycleStatus } from '../../utils/workflowUtils';
import { directSupabase } from '../../lib/supabaseClient';
import { WorkspaceCardHeader } from '../../components/WorkspaceCardHeader';
import { documentsService } from '../../modules/docs/services/documents.service';
import { visualDesignerSurveysService } from '../../modules/docs/services/visualDesignerSurveys.service';
import { listProjectActivityEvents } from '../../services/activityEvents.service';
import {
  buildWorkspaceActivityItems,
  type ActivityItem,
  type ActivityTone,
} from './workspaceRecentActivity';

function formatActivityStamp(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function toneColor(tone: ActivityTone): string {
  if (tone === 'success') return 'success.400';
  if (tone === 'primary') return 'primary.400';
  if (tone === 'danger') return 'danger.400';
  if (tone === 'warning') return 'warning.400';
  return 'neutral.400';
}

async function resolveActorLabels(actorIds: Array<string | null | undefined>): Promise<Map<string, string>> {
  const unique = Array.from(new Set(actorIds.map((id) => String(id || '').trim()).filter(Boolean)));
  const map = new Map<string, string>();
  if (unique.length === 0) return map;
  const { data, error } = await directSupabase
    .from('profiles')
    .select('id, full_name, email')
    .in('id', unique);
  if (error) return map;
  ((data || []) as Array<{ id: string; full_name: string | null; email: string | null }>).forEach((row) => {
    const label = String(row.full_name || row.email || '').trim();
    if (label) map.set(String(row.id), label);
  });
  return map;
}

interface WorkspaceRecentActivityCardProps {
  project: Project;
}

const WorkspaceRecentActivityCard: React.FC<WorkspaceRecentActivityCardProps> = ({ project }) => {
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [auditOpen, setAuditOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const forProject = `metadata->>projectId.eq.${project.id},metadata->>project_id.eq.${project.id}`;
        const [eventsRes, docs, surveys, notifRes, jobRes, emailRes, activityEvents] = await Promise.all([
          directSupabase
            .from('project_events')
            .select('id, event_type, created_at, updated_at')
            .eq('project_id', project.id)
            .order('created_at', { ascending: false })
            .limit(200),
          documentsService.getDocumentsByProject(project.id).catch(() => []),
          visualDesignerSurveysService.listByProject(project.id).catch(() => []),
          directSupabase
            .from('notifications')
            .select('id, type, message, created_at, metadata')
            .or(forProject)
            .order('created_at', { ascending: false })
            .limit(200),
          (directSupabase as any)
            .from('project_job_completions')
            .select('id, action, attested_at, job_complete, attested_by')
            .eq('project_id', project.id)
            .order('attested_at', { ascending: false })
            .limit(200),
          directSupabase
            .from('email_activity_log')
            .select('id, template_type, status, subject, created_at, metadata')
            .or(forProject)
            .order('created_at', { ascending: false })
            .limit(200),
          listProjectActivityEvents(project.id, 500).catch(() => []),
        ]);

        if (cancelled) return;

        const projectId = String(project.id);
        const notifications = ((notifRes.data || []) as Array<Record<string, unknown>>)
          .filter((row) => {
            const meta = row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata)
              ? row.metadata as Record<string, unknown>
              : {};
            return String(meta.projectId || meta.project_id || '') === projectId;
          })
          .map((row) => ({
            id: String(row.id || ''),
            type: row.type ? String(row.type) : null,
            message: row.message ? String(row.message) : null,
            createdAt: row.created_at ? String(row.created_at) : null,
          }));

        const emails = ((emailRes.data || []) as Array<Record<string, unknown>>)
          .filter((row) => {
            const meta = row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata)
              ? row.metadata as Record<string, unknown>
              : {};
            return String(meta.projectId || meta.project_id || '') === projectId;
          })
          .map((row) => ({
            id: String(row.id || ''),
            templateType: row.template_type ? String(row.template_type) : null,
            status: row.status ? String(row.status) : null,
            subject: row.subject ? String(row.subject) : null,
            createdAt: row.created_at ? String(row.created_at) : null,
            metadata: row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata)
              ? row.metadata as Record<string, unknown>
              : null,
          }));

        const invoiceIds = docs.filter((doc) => String(doc.kind || '').toLowerCase() === 'invoice').map((doc) => doc.id);
        const freeAgentSteps = new Map<string, string[]>();
        if (invoiceIds.length > 0) {
          const { data: syncRows } = await directSupabase
            .from('freeagent_sync_audit_log')
            .select('document_id, metadata')
            .eq('action', 'update')
            .in('document_id', invoiceIds);
          if (cancelled) return;
          ((syncRows || []) as Array<{ document_id: string; metadata: { action?: string } | null }>).forEach((row) => {
            const step = row.metadata?.action;
            if (!step) return;
            freeAgentSteps.set(row.document_id, [...(freeAgentSteps.get(row.document_id) || []), step]);
          });
        }

        const jobRows = (jobRes.data || []) as Array<Record<string, unknown>>;
        const actorNames = await resolveActorLabels([
          ...activityEvents.map((row) => row.actorId),
          ...surveys.flatMap((survey) => [survey.completedBy, survey.reopenedBy]),
          ...jobRows.map((row) => (row.attested_by ? String(row.attested_by) : null)),
        ]);
        if (cancelled) return;
        const nameOf = (id?: string | null) => (id ? actorNames.get(String(id)) || null : null);

        const jobCompletions = jobRows.map((row) => ({
          id: String(row.id || ''),
          action: row.action != null
            ? String(row.action)
            : (row.job_complete === false ? 'released' : 'completed'),
          attestedAt: row.attested_at ? String(row.attested_at) : null,
          actorLabel: nameOf(row.attested_by ? String(row.attested_by) : null),
        }));

        const next = buildWorkspaceActivityItems({
          project,
          projectStatusLabel: workspaceLifecycleStatus(project),
          documents: docs.map((doc) => ({
            id: doc.id,
            kind: doc.kind,
            lifecycleRef: doc.lifecycleRef,
            invoiceRef: doc.invoiceRef,
            status: doc.status,
            createdAt: doc.createdAt,
            statusChangedAt: doc.statusChangedAt,
            sentAt: doc.sentAt,
            acceptedAt: doc.acceptedAt,
            rejectedAt: doc.rejectedAt,
            invoicedAt: doc.invoicedAt,
            paidAt: doc.paidAt,
            voidedAt: doc.voidedAt,
            freeAgentSteps: freeAgentSteps.get(doc.id) || null,
          })),
          surveys: surveys.map((survey) => ({
            id: survey.id,
            kind: survey.kind,
            label: survey.label,
            completionStatus: survey.completionStatus,
            createdAt: survey.createdAt,
            completedAt: survey.completedAt,
            reopenedAt: survey.reopenedAt,
            completedByLabel: nameOf(survey.completedBy),
            reopenedByLabel: nameOf(survey.reopenedBy),
          })),
          events: ((eventsRes.data || []) as Array<Record<string, unknown>>).map((row) => ({
            id: String(row.id || ''),
            eventType: row.event_type ? String(row.event_type) : null,
            createdAt: row.created_at ? String(row.created_at) : null,
            updatedAt: row.updated_at ? String(row.updated_at) : null,
          })),
          notifications,
          jobCompletions,
          emails,
          eventLog: activityEvents.map((row) => ({
            id: row.id,
            occurredAt: row.occurredAt,
            action: row.action,
            label: row.label,
            actorId: row.actorId,
            actorLabel: nameOf(row.actorId),
            metadata: row.metadata,
          })),
          limit: Number.POSITIVE_INFINITY,
        });
        setItems(next);
      } catch (err) {
        console.warn('Failed to load workspace recent activity', err);
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [
    project.cancelled_at,
    project.completed_at,
    project.created_at,
    project.customer_id,
    project.customer_name,
    project.id,
    project.project_status,
    project.status,
    project.status_changed_at,
  ]);

  return (
    <Card variant="outlined">
      <CardContent>
        <WorkspaceCardHeader
          icon={MdOutlineHistory}
          title="Recent activity"
          action={(
            <JoyLink
              component="button"
              type="button"
              level="body-sm"
              disabled={loading || items.length === 0}
              onClick={() => setAuditOpen(true)}
              sx={{ fontWeight: 'lg' }}
            >
              View all
            </JoyLink>
          )}
        />
        {loading ? (
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>Loading activity…</Typography>
        ) : items.length === 0 ? (
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>No activity yet.</Typography>
        ) : (
          <Stack gap={1.25}>
            {items.slice(0, RECENT_LIMIT).map((item) => (
              <Stack key={item.id} direction="row" gap={1.25} alignItems="flex-start">
                <ToneDot tone={item.tone} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography level="body-sm">{item.label}</Typography>
                  {item.actorLabel && !item.actorInLabel ? (
                    <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                      {item.actorLabel}
                    </Typography>
                  ) : null}
                </Box>
                <Typography level="body-xs" sx={{ color: 'neutral.500', whiteSpace: 'nowrap' }}>
                  {formatActivityStamp(item.at)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </CardContent>
      <ActivityAuditModal open={auditOpen} items={items} onClose={() => setAuditOpen(false)} />
    </Card>
  );
};

const RECENT_LIMIT = 10;

function ToneDot({ tone }: { tone: ActivityTone }) {
  return (
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        mt: '6px',
        flexShrink: 0,
        bgcolor: toneColor(tone),
      }}
    />
  );
}

function dayHeading(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown date';
  return date.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/London',
  });
}

function timeOnly(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/London' });
}

function ActivityAuditModal({ open, items, onClose }: { open: boolean; items: ActivityItem[]; onClose: () => void }) {
  const days = useMemo(() => {
    const groups: Array<{ heading: string; items: ActivityItem[] }> = [];
    items.forEach((item) => {
      const heading = dayHeading(item.at);
      const last = groups[groups.length - 1];
      if (last && last.heading === heading) last.items.push(item);
      else groups.push({ heading, items: [item] });
    });
    return groups;
  }, [items]);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        layout="center"
        sx={{ width: 'min(720px, 96vw)', maxHeight: '88vh', p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        <ModalClose />
        <Box sx={{ px: 2.5, pt: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography level="title-lg">Activity</Typography>
          <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
            {items.length} {items.length === 1 ? 'entry' : 'entries'} on this workspace, newest first.
          </Typography>
        </Box>
        <Box sx={{ overflowY: 'auto', px: 2.5, py: 1.5 }}>
          <Stack gap={2}>
            {days.map((day) => (
              <Box key={day.heading}>
                <Typography
                  level="body-xs"
                  sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'neutral.500', mb: 1 }}
                >
                  {day.heading}
                </Typography>
                <Stack gap={1.25}>
                  {day.items.map((item) => (
                    <Stack key={item.id} direction="row" gap={1.25} alignItems="flex-start">
                      <ToneDot tone={item.tone} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography level="body-sm">{item.label}</Typography>
                        {(item.details || []).map((detail) => (
                          <Typography key={detail} level="body-xs" sx={{ color: 'text.secondary' }}>{detail}</Typography>
                        ))}
                        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                          {[timeOnly(item.at), item.actorLabel ? `by ${item.actorLabel}` : null].filter(Boolean).join(' · ')}
                        </Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      </ModalDialog>
    </Modal>
  );
}

export default WorkspaceRecentActivityCard;
